// src/screens/CommitteeDashboard.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  updateDoc,
  onSnapshot,
  getDocs,
  collection,
  query,
  where,
} from "firebase/firestore";
import { db, auth } from "../firebase";

function CommitteeDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [committee, setCommittee] = useState(null);

  // Motion states
  const [motionName, setMotionName] = useState("");
  const [raisedBy, setRaisedBy] = useState("");
  const [perSpeakerTime, setPerSpeakerTime] = useState("");
  const [totalTime, setTotalTime] = useState("");

  // Countries
  const [countries, setCountries] = useState([]);
  const [activeCountries, setActiveCountries] = useState({});
  const [newCountries, setNewCountries] = useState("");
  const [search, setSearch] = useState("");

  // Access management
  const [accessEmail, setAccessEmail] = useState("");
  const [accessList, setAccessList] = useState([]);
  const [showPermissionPopup, setShowPermissionPopup] = useState(false);
  const [tempPermissions, setTempPermissions] = useState({
    view: true,
    write: false,
  });

  // Permissions
  const [perms, setPerms] = useState({ view: false, write: false });

  // Real-time listener for committee
  useEffect(() => {
    const docRef = doc(db, "committees", id);
    const unsub = onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();

        // check if current user has access
        if (
          data.createdBy !== auth.currentUser?.uid &&
          !data.accessList?.some((u) => u.email === auth.currentUser?.email)
        ) {
          alert("You do not have access to this committee.");
          navigate("/main");
          return;
        }

        setCommittee({ id: snapshot.id, ...data });
        setActiveCountries(data.countries || {});
        setCountries(
          Object.keys(data.countries || {}).sort((a, b) =>
            a.localeCompare(b)
          )
        );
        setAccessList(data.accessList || []);

        // set permissions
        if (data.createdBy === auth.currentUser?.uid) {
          setPerms({ view: true, write: true });
        } else {
          const entry = (data.accessList || []).find(
            (u) => u.email === auth.currentUser?.email
          );
          setPerms(entry?.permissions || { view: false, write: false });
        }
      }
    });
    return () => unsub();
  }, [id, navigate]);

  // Save motion (add to motions array)
  const saveMotion = async () => {
    if (!perms.write) return;
    if (!motionName.trim() || !raisedBy || !perSpeakerTime || !totalTime) {
      alert("Please fill all motion fields before saving.");
      return;
    }

    const newMotion = {
      id: Date.now().toString(),
      name: motionName,
      raisedBy,
      perSpeakerTime,
      totalTime,
    };

    const updatedMotions = [...(committee.motions || []), newMotion];

    const ref = doc(db, "committees", id);
    await updateDoc(ref, { motions: updatedMotions });

    setMotionName("");
    setRaisedBy("");
    setPerSpeakerTime("");
    setTotalTime("");
  };

  // Remove motion
  const removeMotion = async (motionId) => {
    if (!perms.write) return;
    const updatedMotions = (committee.motions || []).filter(
      (m) => m.id !== motionId
    );
    const ref = doc(db, "committees", id);
    await updateDoc(ref, { motions: updatedMotions });
  };

  // Add new countries
  const addCountries = async () => {
    if (!perms.write) return;
    if (!newCountries.trim()) return;
    const splitCountries = newCountries
      .split(",")
      .map((c) => c.trim())
      .filter((c) => c.length > 0);

    const updated = { ...activeCountries };
    splitCountries.forEach((c) => {
      if (!updated[c]) updated[c] = "Present"; // default
    });

    const sortedKeys = Object.keys(updated).sort((a, b) => a.localeCompare(b));
    setCountries(sortedKeys);
    setActiveCountries(updated);
    setNewCountries("");

    const ref = doc(db, "committees", id);
    await updateDoc(ref, { countries: updated });
  };

  // Remove country
  const removeCountry = async (country) => {
    if (!perms.write) return;
    const updated = { ...activeCountries };
    delete updated[country];
    const sortedKeys = Object.keys(updated).sort((a, b) => a.localeCompare(b));
    setCountries(sortedKeys);
    setActiveCountries(updated);

    const ref = doc(db, "committees", id);
    await updateDoc(ref, { countries: updated });
  };

  // Update attendance
  const updateAttendance = async (country, status) => {
    if (!perms.write) return;
    const updated = { ...activeCountries, [country]: status };
    setActiveCountries(updated);

    const ref = doc(db, "committees", id);
    await updateDoc(ref, { countries: updated });
  };

  // Add access step 1: verify user exists
  const addAccess = async () => {
    if (!perms.write) return;
    if (!accessEmail.trim()) return;

    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", accessEmail.trim()));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      alert("User with this email does not exist.");
      return;
    }

    // open popup for permissions
    setShowPermissionPopup(true);
  };

  // Add access step 2: confirm with selected permissions
  const confirmAddAccess = async () => {
    if (!perms.write) return;
    const updated = [
      ...(committee.accessList || []).filter(
        (u) => u.email !== accessEmail.trim()
      ),
      { email: accessEmail.trim(), permissions: tempPermissions },
    ];

    const ref = doc(db, "committees", id);
    await updateDoc(ref, { accessList: updated });

    setAccessEmail("");
    setTempPermissions({ view: true, write: false });
    setShowPermissionPopup(false);
  };

  // Remove access with confirmation
  const removeAccess = async (email) => {
    if (!perms.write) return;

    const confirmed = window.confirm(
      "Are you sure you want to remove this user's access? This action cannot be undone."
    );
    if (!confirmed) return;

    const updated = (committee.accessList || []).filter(
      (u) => u.email !== email
    );
    const ref = doc(db, "committees", id);
    await updateDoc(ref, { accessList: updated });
  };

  if (!committee) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-500">
        Loading committee...
      </div>
    );
  }

  const filteredCountries = countries.filter((c) =>
    c.toLowerCase().includes(search.toLowerCase())
  );

  // ✅ Attendance summary calculations
  const countryStatuses = committee.countries || {};
  const totalCountries = Object.keys(countryStatuses).length;
  const presentCountries = Object.entries(countryStatuses).filter(
    ([, status]) => status?.toLowerCase().includes("present")
  ).length;
  const simpleMajority = Math.floor(presentCountries / 2) + 1;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 pb-32">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
          {committee.name} Dashboard
        </h1>
        <button
          onClick={() => navigate("/main")}
          className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition"
        >
          Back
        </button>
      </div>

      {/* Motion */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-xl p-6 mb-6">
        <h2 className="text-xl font-semibold mb-3 dark:text-gray-100">Motions</h2>
        {countries.length === 0 && perms.write && (
          <p className="text-red-500 mb-3">
            ⚠️ Please add countries before raising a motion.
          </p>
        )}

        {perms.write && (
          <div className="flex flex-col gap-3 mb-6">
            <input
              type="text"
              value={motionName}
              onChange={(e) => setMotionName(e.target.value)}
              placeholder="Enter motion/topic..."
              className="border px-3 py-2 rounded-lg dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            />
            <select
              value={raisedBy}
              onChange={(e) => setRaisedBy(e.target.value)}
              className="border px-3 py-2 rounded-lg dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            >
              <option value="">-- Select Raised By --</option>
              {countries.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <div className="flex gap-3">
              <input
                type="number"
                value={perSpeakerTime}
                onChange={(e) => setPerSpeakerTime(e.target.value)}
                placeholder="Per speaker time (minutes)"
                className="flex-1 border px-3 py-2 rounded-lg dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
              />
              <input
                type="number"
                value={totalTime}
                onChange={(e) => setTotalTime(e.target.value)}
                placeholder="Total time (minutes)"
                className="flex-1 border px-3 py-2 rounded-lg dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
              />
            </div>
            <button
              onClick={saveMotion}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Save Motion
            </button>
          </div>
        )}

        {/* Saved motions */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(committee.motions || []).map((motion) => (
            <div
              key={motion.id}
              className="border rounded-xl p-4 shadow bg-gray-50 dark:bg-gray-700 flex flex-col dark:border-gray-600"
            >
              <h3 className="text-lg font-semibold dark:text-gray-100">
                {motion.name}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Raised by: {motion.raisedBy}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Per speaker: {motion.perSpeakerTime} min
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Total time: {motion.totalTime} min
              </p>
              <div className="flex justify-between mt-3">
                <button
                  onClick={() => navigate(`/committee/${id}/motion/${motion.id}`)}
                  className="bg-purple-600 text-white px-3 py-1 rounded-lg hover:bg-purple-700 text-sm"
                >
                  Manage
                </button>
                {perms.write && (
                  <button
                    onClick={() => {
                      const confirmed = window.confirm(
                        `Are you sure you want to delete the motion "${motion.name}"?`
                      );
                      if (confirmed) removeMotion(motion.id);
                    }}
                    className="bg-red-500 text-white px-3 py-1 rounded-lg hover:bg-red-600 text-sm"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Country Management */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-3 dark:text-gray-100">
          Countries
        </h2>


        {/* Add countries */}
        {perms.write && (
          <div className="flex gap-3 mb-4">
            <input
              type="text"
              placeholder="Add countries (comma separated)..."
              value={newCountries}
              onChange={(e) => setNewCountries(e.target.value)}
              className="flex-1 border px-3 py-2 rounded-lg dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            />
            <button
              onClick={addCountries}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              Add
            </button>
          </div>
        )}

        {/* Search */}
        <div className="flex justify-between items-center mb-3">
          <input
            type="text"
            placeholder="Search country..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border px-3 py-2 rounded-lg w-full dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
          />
        </div>

        {/* List */}
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[500px] overflow-y-auto">
          {filteredCountries.map((country) => (
            <div
              key={country}
              className="flex justify-between items-center border rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
            >
              <span className="font-medium dark:text-gray-100">{country}</span>
              <div className="flex items-center gap-2">
                <select
                  value={activeCountries[country] || "Present"}
                  onChange={(e) => updateAttendance(country, e.target.value)}
                  disabled={!perms.write}
                  className="border rounded px-2 py-1 text-sm dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                >
                  <option value="Present">Present</option>
                  <option value="Present & Voting">Present & Voting</option>
                  <option value="Absent">Absent</option>
                </select>
                {perms.write && (
                  <button
                    onClick={() => removeCountry(country)}
                    className="text-red-500 hover:text-red-700"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Attendance Summary */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-xl p-6 mb-6 border border-gray-100 dark:border-gray-700">
          <h2 className="text-xl font-semibold mb-3 dark:text-gray-100">
            Attendance Summary
          </h2>
          <p className="text-gray-700 dark:text-gray-300">
            Total Countries: <b>{totalCountries}</b>
          </p>
          <p className="text-gray-700 dark:text-gray-300">
            Present (including Present & Voting): <b>{presentCountries}</b>
          </p>
          <p className="text-gray-700 dark:text-gray-300">
            Simple Majority: <b>{simpleMajority}</b>
          </p>
        </div>

      </div>

      {/* Access Management */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-xl p-6 mt-6">
        <h2 className="text-xl font-semibold mb-3 dark:text-gray-100">
          Access Management
        </h2>

        {/* Add access */}
        {perms.write && (
          <div className="flex gap-3 mb-4">
            <input
              type="email"
              placeholder="Enter user email..."
              value={accessEmail}
              onChange={(e) => setAccessEmail(e.target.value)}
              className="flex-1 border px-3 py-2 rounded-lg dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            />
            <button
              onClick={addAccess}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Add
            </button>
          </div>
        )}

        {/* List of users with access */}
        {accessList.length === 0 ? (
          <p className="text-gray-400 italic">No one else has access yet.</p>
        ) : (
          <ul className="space-y-2">
            {accessList.map((u) => (
              <li
                key={u.email}
                className="flex justify-between items-center border rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
              >
                <span className="dark:text-gray-100">
                  {u.email}{" "}
                  <span className="text-sm text-gray-500 dark:text-gray-300">
                    ({Object.keys(u.permissions || {})
                      .filter((p) => u.permissions[p])
                      .join(", ")})
                  </span>
                </span>
                {perms.write && (
                  <button
                    onClick={() => removeAccess(u.email)}
                    className="text-red-500 hover:text-red-700"
                  >
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Permission popup */}
      {showPermissionPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-xl w-96">
            <h3 className="text-lg font-semibold mb-4 dark:text-gray-100">
              Select Permissions for {accessEmail}
            </h3>
            <div className="flex flex-col gap-2 mb-4">
              <label className="flex items-center gap-2 dark:text-gray-100">
                <input
                  type="checkbox"
                  checked={tempPermissions.view}
                  onChange={(e) =>
                    setTempPermissions((prev) => ({
                      ...prev,
                      view: e.target.checked,
                    }))
                  }
                />
                View
              </label>
              <label className="flex items-center gap-2 dark:text-gray-100">
                <input
                  type="checkbox"
                  checked={tempPermissions.write}
                  onChange={(e) =>
                    setTempPermissions((prev) => ({
                      ...prev,
                      write: e.target.checked,
                    }))
                  }
                />
                Write (full access)
              </label>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowPermissionPopup(false)}
                className="px-4 py-2 rounded-lg bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600 dark:text-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={confirmAddAccess}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommitteeDashboard;